import { zodResolver } from "@hookform/resolvers/zod";
import {
    type CustomAttributeListOutput,
    type CustomAttributeType,
    customAttributeTypeValues,
} from "@orsane/schema/tasks";
import { Plus, SquareDashed, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpcClient } from "@/lib/trpc";
import EnterButton from "../buttons/EnterButton";
import { Field, FieldDescription, FieldError, FieldLabel } from "../ui/field";

// Derived from the shared enum rather than restated, so this list cannot drift out of
// sync with it again. Two types are excluded on purpose: orbit_support_project is
// provisioned by the Orbit connector, and select_multiple has no editor yet.
const creatableAttributeTypeValues = customAttributeTypeValues.filter(
    (type) => type !== "orbit_support_project" && type !== "select_multiple"
) as [CustomAttributeType, ...CustomAttributeType[]];

const attributeFormSchema = z.object({
    name: z.string().min(1, "Name is required").max(100, "Name cannot exceed 100 characters"),
    type: z.enum(creatableAttributeTypeValues),
    description: z.string().max(500, "Description cannot exceed 500 characters").optional(),
    options: z.array(z.string()).optional(),
});

type AttributeFormData = z.infer<typeof attributeFormSchema>;

interface AttributeDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    attribute?: CustomAttributeListOutput;
    onSubmit: (data: AttributeFormData & { id?: number }) => void;
    isLoading?: boolean;
}

export function AttributeDialog({ open, onOpenChange, attribute, onSubmit, isLoading = false }: AttributeDialogProps) {
    const [newOption, setNewOption] = useState("");
    const isEditing = !!attribute;

    const form = useForm<AttributeFormData>({
        resolver: zodResolver(attributeFormSchema),
        defaultValues: {
            name: "",
            type: "text",
            description: "",
            options: [],
        },
    });

    const selectedType = form.watch("type");
    const options = form.watch("options") || [];
    const showOptions = selectedType === "select_one";

    useEffect(() => {
        async function fetchAttribute() {
            if (attribute && open) {
                const attrib = await trpcClient.customAttributes.get.query({ id: attribute.id });
                form.reset(attrib as AttributeFormData);
            } else if (!open) {
                form.reset({
                    name: "",
                    type: "text",
                    description: "",
                    options: [],
                });
                setNewOption("");
            }
        }
        fetchAttribute();
    }, [attribute, open, form]);

    const handleSubmit = (data: AttributeFormData) => {
        const submitData = {
            ...data,
            description: data.description || "",
            options: data.options || [],
            ...(isEditing && { id: attribute.id }),
        };
        onSubmit(submitData);
    };

    const addOption = () => {
        if (newOption.trim()) {
            const currentOptions = form.getValues("options") || [];
            if (!currentOptions.includes(newOption.trim())) {
                form.setValue("options", [...currentOptions, newOption.trim()]);
            }
            setNewOption("");
        }
    };

    const removeOption = (optionToRemove: string) => {
        const currentOptions = form.getValues("options") || [];
        form.setValue(
            "options",
            currentOptions.filter((option) => option !== optionToRemove)
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addOption();
        }
    };

    const getTypeLabel = (type: string) => {
        const typeLabels: Record<string, string> = {
            text: "Text",
            number: "Number",
            select_one: "Dropdown",
            timer: "Timer",
            orbit_support_project: "Orbit Support Project",
        };
        return typeLabels[type] || type;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px] gap-6 rounded-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg font-medium">
                        <SquareDashed className="size-4" />
                        {isEditing ? "Edit Custom Attribute" : "Create Custom Attribute"}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? "Update the custom attribute details."
                            : "Add a new custom attribute to organize your tasks."}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                    <Controller
                        control={form.control}
                        name="name"
                        render={({ field, fieldState }) => (
                            <Field>
                                <FieldLabel>Name</FieldLabel>
                                <Input data-1p-ignore placeholder="Attribute name" {...field} />
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="type"
                        render={({ field, fieldState }) => (
                            <Field className="gap-1 w-40">
                                <FieldLabel>Type</FieldLabel>
                                {/* Controlled, not defaultValue: editing loads the attribute asynchronously,
                                    so the Select mounts on the form default and would never pick the
                                    real type up from the reset that follows. */}
                                <Select value={field.value} onValueChange={field.onChange} disabled={isEditing}>
                                    <SelectTrigger className="min-w-36">
                                        <SelectValue>{(v: string) => getTypeLabel(v)}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            {creatableAttributeTypeValues.map((type) => (
                                                <SelectItem key={type} value={type}>
                                                    {getTypeLabel(type)}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    <Controller
                        control={form.control}
                        name="description"
                        render={({ field, fieldState }) => (
                            <Field>
                                <FieldLabel>Description (Optional)</FieldLabel>
                                <Textarea placeholder="Describe what this attribute is for..." {...field} />
                                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                            </Field>
                        )}
                    />

                    {showOptions && (
                        <Controller
                            control={form.control}
                            name="options"
                            render={({ fieldState }) => (
                                <Field>
                                    <FieldLabel>Options</FieldLabel>
                                    <FieldDescription>Add options for this select field</FieldDescription>
                                    <div className="flex gap-2">
                                        <Input
                                            placeholder="Add option"
                                            value={newOption}
                                            onChange={(e) => setNewOption(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={addOption}
                                            disabled={!newOption.trim()}
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    {options.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {options.map((option) => (
                                                <Badge key={option} variant="outline" className="gap-1">
                                                    {option}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-4 w-4 p-0 hover:bg-transparent"
                                                        onClick={() => removeOption(option)}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                                </Field>
                            )}
                        />
                    )}

                    <DialogFooter>
                        <Button size="sm" type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <EnterButton type="submit" disabled={isLoading} isSubmitting={isLoading}>
                            {isLoading ? "Saving..." : isEditing ? "Update" : "Create"}
                        </EnterButton>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
